import React from 'react';
import Link from 'next/link';
import LicenseNotice from './LicenseNotice';

/** @jsxRuntime classic */
/** @jsx React.createElement */
// ─── CSS injected into <head> ────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;700;900&family=Playfair+Display:ital,wght@0,900;1,900&display=swap');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

:root {
  --teal: #00c2cc;
  --cyan: #00e5ff;
  --navy: #08111f;
  --white: #f0f4ff;
  --muted: rgba(176,196,222,0.72);
}

html, body, #root {
  height: 100%;
  overflow-x: hidden;
  background: var(--navy);
  font-family: 'Outfit', sans-serif;
}

/* ── KEYFRAMES ── */
@keyframes bg-shift {
  from { filter: brightness(1); }
  to   { filter: brightness(1.12) saturate(1.1); }
}
@keyframes glow-pulse {
  from { opacity: 0.6; }
  to   { opacity: 1; }
}
@keyframes blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
}
@keyframes bounce-down {
  0%, 100% { transform: rotate(45deg) translate(0,0);     opacity: 1; }
  60%       { transform: rotate(45deg) translate(4px,4px); opacity: 0.4; }
}
@keyframes promo-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ── HERO ── */
.hero {
  position: relative;
  height: 100vh;
  min-height: 560px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  overflow: hidden;
  padding: 0 0 92px;
}

.hero-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.hero-video-fallback {
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(ellipse 80% 60% at 60% 40%, rgba(0,100,120,0.45) 0%, transparent 60%),
    radial-gradient(ellipse 50% 50% at 20% 80%, rgba(0,50,100,0.3) 0%, transparent 55%),
    linear-gradient(160deg, #081828 0%, #0a1f3a 40%, #06121e 100%);
  animation: bg-shift 12s ease-in-out infinite alternate;
}

.hero-grid {
  position: absolute;
  inset: 0;
  z-index: 1;
  background-image:
    linear-gradient(rgba(0,194,204,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,194,204,0.04) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 80%);
  pointer-events: none;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(
    180deg,
    rgba(8,17,31,0.45) 0%,
    rgba(8,17,31,0.6) 32%,
    rgba(8,17,31,0.9) 70%,
    rgba(8,17,31,0.95) 100%
  );
  pointer-events: none;
  // backdrop-filter: blur(3px);
  // -webkit-backdrop-filter: blur(3px);
}

.hero-glow {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: radial-gradient(ellipse 55% 45% at 50% 55%, rgba(0,194,204,0.1) 0%, transparent 65%);
  animation: glow-pulse 4s ease-in-out infinite alternate;
  pointer-events: none;
}

/* ── PROMO STRIP ── */
.promo-strip {
  position: absolute;
  top: 0; left: 0; right: 0;
  z-index: 10;
  height: 36px;
  background: rgba(0,194,204,0.12);
  border-bottom: 1px solid rgba(0,194,204,0.2);
  overflow: hidden;
  display: flex;
  align-items: center;
}

.promo-track {
  display: flex;
  gap: 80px;
  white-space: nowrap;
  animation: promo-scroll 28s linear infinite;
}

.promo-item {
  font-size: 12px;
  font-weight: 600;
  color: var(--teal);
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.promo-badge {
  background: var(--teal);
  color: var(--navy);
  font-size: 9px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 100px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* ── HERO LOGO ── */
.hero-logo {
  position: absolute;
  top: 36px;
  left: 48px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  animation: fadeIn 0.8s ease both;
}

/* ── HERO CONTENT ── */
.hero-content-wrap {
  position: relative;
  z-index: 5;
  width: clamp(900px, 80vw, 100%);
  padding: 0 48px;
}

.hero-content {
  position: relative;
  text-align: left;
  padding: 28px 0 0;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  animation: fadeUp 0.7s 0.1s ease both;
}

.eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--teal);
  animation: blink 2s ease-in-out infinite;
}

.eyebrow-text {
  font-size: 11px;
  font-weight: 600;
  color: var(--teal);
  letter-spacing: 3px;
  text-transform: uppercase;
}

.hero-h1 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(48px, 6vw, 108px);
  font-weight: 900;
  line-height: 0.96;
  letter-spacing: -2px;
  color: var(--white);
  margin-bottom: 22px;
  animation: fadeUp 0.7s 0.18s ease both;
  text-shadow: 0 6px 24px rgba(0,0,0,0.32);
}

.hero-h1 em {
  font-style: italic;
  background: linear-gradient(90deg, #d91730, #e61832);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-sub {
  font-size: clamp(14px, 1.6vw, 18px);
  color: var(--muted);
  font-weight: 300;
  letter-spacing: 0.3px;
  line-height: 1.65;
  animation: fadeUp 0.7s 0.26s ease both;
  max-width: 620px;
}

/* ── SCROLL CUE ── */
.scroll-cue {
  position: absolute;
  bottom: 32px;
  right: 48px;
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  animation: fadeIn 1s 0.8s ease both;
  cursor: pointer;
}

.scroll-cue-label {
  font-size: 10px;
  color: var(--muted);
  letter-spacing: 2.5px;
  text-transform: uppercase;
  font-weight: 500;
}

.scroll-cue-arrow {
  width: 22px;
  height: 22px;
  border-right: 1.5px solid var(--teal);
  border-bottom: 1.5px solid var(--teal);
  transform: rotate(45deg);
  animation: bounce-down 1.8s ease-in-out infinite;
}

/* =========================
   Right Side Creative Blocks
========================= */

.banner-blocks{
    position: absolute;
    top: 0;
    right: 0;
    height: 100vh;

    width: clamp(190px, 16vw, 320px);

    display: flex;
    flex-direction: column;

    z-index: 10;

    overflow: hidden;
	gap:10px;
}

/* Creative glowing overlay */
.banner-blocks::before{
    content: "";
    position: absolute;
    inset: 0;

    /*background:
        linear-gradient(
            180deg,
            rgba(0, 183, 255, 0.18),
            rgba(123, 97, 255, 0.14),
            rgba(255, 0, 128, 0.12)
        );

    backdrop-filter: blur(12px);*/
    z-index: 0;
}

/* Animated gradient glow */
.banner-blocks::after{
    content: "";
    position: absolute;
    width: 220%;
    height: 220%;

    background:
        radial-gradient(circle, rgba(0,255,255,0.12) 0%, transparent 60%),
        radial-gradient(circle, rgba(255,0,140,0.14) 0%, transparent 65%);

    top: -50%;
    left: -50%;

    animation: rotateGlow 14s linear infinite;

    z-index: 0;
}

/* Block Style */
.banner-blocks a{
    position: relative;
    z-index: 2;

    flex: 1;

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    padding: 24px;

    color: #fff;

    font-size: clamp(15px, 1vw, 21px);
    font-weight: 600;
    letter-spacing: 0.5px;

    text-align: center;

    border-bottom: 1px solid rgba(255,255,255,0.08);

    // background: rgba(255,255,255,0.04);

    // backdrop-filter: blur(14px);

    background: rgba(255, 255, 255, 0.04);
    backdrop-filter: blur(4px);

    transition: all 0.4s ease;

    cursor: pointer;

    overflow: hidden;
    text-decoration: none;
}

.banner-blocks a img{
    height: 40px;
    width: 60px;
}

/* Hover Glow */
.banner-blocks a:hover{
    background: rgba(255,255,255,0.08);

    transform: translateX(-8px);

    box-shadow:
        -10px 0 30px rgba(0,255,255,0.15),
        -10px 0 40px rgba(255,0,128,0.12);
}

/* Shine Effect */
.banner-blocks a::before{
    content: "";

    position: absolute;

    width: 180%;
    height: 100%;

    background: linear-gradient(
        120deg,
        transparent 20%,
        rgba(255,255,255,0.12) 50%,
        transparent 80%
    );

    left: -180%;
    top: 0;

    transition: all 0.7s ease;
}

.banner-blocks a:hover::before{
    left: 120%;
}

/* Glow Animation */
@keyframes rotateGlow{
    from{
        transform: rotate(0deg);
    }
    to{
        transform: rotate(360deg);
    }
}

/* ── SERVICES GRID ── */
.services {
  min-height: 60vh;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr auto;
}

.service-block {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  padding: 48px 52px;
  cursor: pointer;
  text-decoration: none;
  color: var(--white);
  min-height: 320px;
  transition: transform 0.35s ease;
  isolation: isolate;
}

.block-stock  { pointer-events: none; background: linear-gradient(145deg,#061a28 0%,#082232 60%,#04121e 100%); --accent:#00c2cc; --accent-glow:rgba(0,194,204,0.18); }
.block-photo  { background: linear-gradient(145deg,#1a1006 0%,#221408 60%,#120c04 100%); --accent:#f59e0b; --accent-glow:rgba(245,158,11,0.15); }
.block-ai     { background: linear-gradient(145deg,#100820 0%,#180c2e 60%,#0c0618 100%); --accent:#a78bfa; --accent-glow:rgba(167,139,250,0.15); }
.block-quicxy { background: linear-gradient(145deg,#1e0806 0%,#280a06 60%,#160604 100%); --accent:#f87171; --accent-glow:rgba(248,113,113,0.15); }

.services-highlight {
  grid-column: 1 / -1;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 48px 32px;
  background:
    radial-gradient(circle at 18% 20%, rgba(0,194,204,0.18) 0%, transparent 32%),
    radial-gradient(circle at 82% 34%, rgba(248,113,113,0.18) 0%, transparent 30%),
    linear-gradient(135deg, #071522 0%, #130a22 50%, #1d0906 100%);
  border-top: 1px solid rgba(255,255,255,0.07);
  isolation: isolate;
}

.services-highlight::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 48%, transparent 72%);
  opacity: 0.7;
}

.services-highlight-title {
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: 'Playfair Display', serif;
  font-size: clamp(30px, 4.4vw, 64px);
  font-weight: 900;
  line-height: 1.08;
  text-align: center;
  color: transparent;
  /* background: linear-gradient(90deg, #ffffff 0%, #f59e0b 24%, #00c2cc 54%, #f87171 82%, #ffffff 100%);*/
  background: linear-gradient(
        90deg,
        #FFC65B 0%,
        #FFA44F 8%,
        #FF8A63 16%,
        #FD7B73 24%,
        #F86C9D 32%,
        #F563BD 40%,
        #D46CE0 48%,
        #A16EF2 56%,
        #6178F5 64%,
        #2D8CF8 72%,
        #06A9F9 78%,
        #19C8F3 84%,
        #35DECE 90%,
        #39E3A7 96%,
        #12E85D 100%
    );
    -webkit-text-fill-color:transparent;
  -webkit-background-clip: text;
  background-clip: text;
  text-shadow: 0 18px 44px rgba(0,0,0,0.34);
}

.block-stock .block-tag, .block-stock .block-cta{opacity:0;}
.block-stock .block-content{text-align:center;}
.block-stock .block-media-overlay {
    z-index: 1;
    background: linear-gradient(180deg, rgb(8 17 31) 0%, rgb(8 17 31) 25%, rgb(8 17 31) 72%, rgba(8, 17, 31, 0.94) 100%);
}

.block-media, .block-image, .block-video, .block-media-overlay {
  position: absolute;
  inset: 0;
}

.block-media { z-index: 0; overflow: hidden; }

.block-image {
  background-size: cover;
  background-position: center;
  transition: opacity 0.35s ease, transform 0.6s ease;
  opacity: 1;
  transform: scale(1);
}

.block-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease, transform 0.6s ease;
  transform: scale(1.02);
  pointer-events: none;
}

.block-media-overlay {
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(8,17,31,0.4) 0%,
    rgba(8,17,31,0.6) 25%,
    rgba(8,17,31,0.8) 72%,
    rgba(8,17,31,0.94) 100%
  );
  // backdrop-filter: blur(3px); 
  // -webkit-backdrop-filter: blur(3px);
}

.service-block.is-video-active .block-video,
.service-block:hover .block-video,
.service-block:focus-visible .block-video {
  opacity: 1;
  transform: scale(1);
}

.service-block.is-video-active .block-image,
.service-block:hover .block-image,
.service-block:focus-visible .block-image {
  opacity: 0.22;
  transform: scale(1.06);
}

.block-glow {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(ellipse 70% 70% at 80% 20%, var(--accent-glow) 0%, transparent 65%);
  transition: opacity 0.5s;
  opacity: 0.7;
}
.service-block:hover .block-glow { opacity: 1.2; }

.block-icon-bg {
  position: absolute;
  right: -10px;
  top: -10px;
  z-index: 1;
  font-size: 160px;
  opacity: 0.055;
  line-height: 1;
  user-select: none;
  transition: transform 0.6s ease, opacity 0.4s;
  filter: grayscale(0.5);
  display:none;
}
.service-block:hover .block-icon-bg {
  transform: scale(1.1) rotate(-6deg);
  opacity: 0.1;
}

.block-accent-line {
  position: absolute;
  top: 0; left: 0; right: 0;
  z-index: 2;
  height: 2.5px;
  background: linear-gradient(90deg, var(--accent), transparent 70%);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94);
}
.service-block:hover .block-accent-line { transform: scaleX(1); }

.service-block:nth-child(1) { border-right: 1px solid rgba(255,255,255,0.05); border-bottom: 1px solid rgba(255,255,255,0.05); }
.service-block:nth-child(2) { border-bottom: 1px solid rgba(255,255,255,0.05); }
.service-block:nth-child(3) { border-right: 1px solid rgba(255,255,255,0.05); }

.block-content { position: relative; z-index: 3; }

.block-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.block-tag-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--accent);
}

.block-tag-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: 2.5px;
  text-transform: uppercase;
}

.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(248,113,113,0.15);
  border: 1px solid rgba(248,113,113,0.3);
  border-radius: 100px;
  padding: 3px 10px;
  margin-left: 10px;
  font-size: 9px;
  font-weight: 800;
  color: #f87171;
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

.live-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f87171;
  animation: blink 1s steps(1) infinite;
}

.block-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1.1;
  margin-bottom: 12px;
  text-shadow: 0 3px 16px rgba(0,0,0,0.25);
}

.block-desc {
  font-size: 14px;
  color: var(--white);
  line-height: 1.7;
  font-weight: 300;
  max-width: 360px;
  margin-bottom: 28px;
}

.block-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--accent);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: gap 0.3s;
}
.service-block:hover .block-cta { gap: 16px; }

.block-arrow {
  width: 30px;
  height: 30px;
  border: 1.5px solid var(--accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: background 0.3s, transform 0.3s;
}

.service-block:hover .block-arrow {
  background: var(--accent);
  color: var(--navy);
  transform: rotate(-40deg);
}

/* ── RESPONSIVE ── */
@media (prefers-reduced-motion: reduce) {
  .hero-video, .block-video { display: none; }
  .promo-track, .hero-glow, .scroll-cue-arrow, .eyebrow-dot, .live-dot {
    animation: none !important;
  }
}

@media (max-width: 768px) {
  .services {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, auto);
  }
  .service-block { min-height: 260px; padding: 36px 32px; }
  .services-highlight { min-height: 240px; padding: 44px 24px; }
  .services-highlight-title { font-size: clamp(30px, 9vw, 44px); }
  .service-block:nth-child(1),
  .service-block:nth-child(3) { border-right: none; }
  .service-block:nth-child(1),
  .service-block:nth-child(2),
  .service-block:nth-child(3) { border-bottom: 1px solid rgba(255,255,255,0.05); }
  .hero { padding-bottom: 84px; }
  .hero-h1 { font-size: clamp(42px, 11vw, 72px); }
  .hero-logo { top: 46px; left: 24px; }
  .hero-content-wrap { padding: 0 24px; }
  .scroll-cue { right: 24px; bottom: 24px; }
}
`;

// ─── DATA ────────────────────────────────────────────────────────────────────

const SERVICES = [
  
  {
    id: 'photography',
    href: '/book_photographer',
    blockClass: 'service-block block-photo service-video-card',
    image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=1400&q=80',
    videoSrc: '/static/img/photography.mp4',
    icon: '📸',
    tag: 'Book a Creator',
    title: 'Photography Services',
    desc: 'Connect with verified professional photographers for events, products, portraits, and brand shoots - curated talent, seamless booking.',
    cta: 'Book a Shoot',
    live: false,
  },
  {
    id: 'ai-face-sync',
    href: '/ai_face_sync',
    blockClass: 'service-block block-ai service-video-card',
    image: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=1400&q=80',
    videoSrc: '/static/img/ai-face-sync.mp4',
    icon: '🧠',
    tag: 'AI Generation',
    title: 'AI Face Sync',
    desc: 'Generate stunning AI portraits and professional headshots using your own face. Powered by cutting-edge generative AI - no studio needed.',
    cta: 'Try AI Face Sync',
    live: false,
  },
  {
    id: 'InstaShot',
    href: '/instashot',
    blockClass: 'service-block block-quicxy service-video-card',
    image: '/static/img/instashot-bg.jpg',
    videoSrc: '/static/img/instashot.mp4',
    icon: '🎬',
    tag: 'On-The-Spot Reels',
    title: 'InstaShot',
    desc: 'Book a creator, get filmed and edited reels delivered on the spot - same day. Weddings, birthdays, brand shoots, and events covered.',
    cta: 'Book a Reel Shoot',
    live: true,
  },
  // {
  //   id: 'stock',
  //   href: '/stock-images',
  //   blockClass: 'service-block block-stock service-video-card pointer-events-none ',
  //   image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80',
  //   videoSrc: '/static/img/stock-images.mp4',
  //   // icon: '🖼️',
  //   // tag: 'Browse & License',
  //   title: ' "A complete event memory experience by Picxy "',
  //   // desc: "India's largest curated photo library. Authentic imagery from photographers across the country - landscapes, culture, people, and more.",
  //   // cta: 'Browse Library',
  //   live: false,
  // },
];

// ─── SVG LOGO ────────────────────────────────────────────────────────────────
function PicxyLogo() {
  return React.createElement(
    'svg',
    { width: 72, height: 29, viewBox: '0 0 72 29', fill: 'none', xmlns: 'http://www.w3.org/2000/svg' },
    React.createElement('path', { d: 'M1.3638 0.0220702C1.14233 0.0337266 1.04908 0.0570395 0.862576 0.150291C0.466257 0.325138 0.186503 0.674831 0.0466257 1.14109C0 1.3509 0 0.231886 0 13.7767V22.2859H1.56196H3.12392V14.1846H4.46441C6.83067 14.1846 8.52085 14.138 9.06871 14.0681C9.94294 13.9632 10.5607 13.7883 11.2018 13.4736C12.1577 13.0073 12.9503 12.308 13.5797 11.3754C14.2558 10.3613 14.5939 9.47544 14.7454 8.23986C14.8037 7.692 14.8153 6.44477 14.7454 5.89691C14.5356 4.12514 13.7313 2.63311 12.4258 1.56072C11.6681 0.942929 10.619 0.441702 9.61656 0.22023C9.27852 0.150291 8.84723 0.0803524 8.25276 0.0220702C7.84478 -0.00124268 1.80675 -0.0128991 1.3638 0.0220702ZM7.72822 2.83127C8.45092 2.88955 9.16196 3.14599 9.76809 3.54231C10.0362 3.71716 10.5491 4.21839 10.7705 4.5098C11.2485 5.1509 11.5166 5.85029 11.5982 6.71286C11.6331 7.08587 11.6331 7.03924 11.5982 7.41225C11.5166 8.27483 11.2485 8.96256 10.7705 9.61532C10.5491 9.90673 10.0362 10.408 9.76809 10.5828C9.17362 10.9791 8.47423 11.2239 7.71656 11.2938C7.34355 11.3288 7.39018 11.3288 7.02883 11.2938C6.27116 11.2239 5.57178 10.9791 4.9773 10.5828C4.7092 10.408 4.19632 9.90673 3.97484 9.61532C3.49693 8.97421 3.22883 8.27483 3.14724 7.41225C3.11227 7.03924 3.11227 7.08587 3.14724 6.71286C3.22883 5.85029 3.49693 5.16256 3.97484 4.5098C4.19632 4.21839 4.7092 3.71716 4.9773 3.54231C5.57178 3.14599 6.48098 2.84293 7.0638 2.83127C7.1454 2.83127 7.23865 2.81961 7.28527 2.80796C7.3319 2.7963 7.37852 2.7963 7.40184 2.80796C7.42515 2.81961 7.57668 2.83127 7.72822 2.84293V2.83127Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M7.34344 4.55642C6.73731 4.75458 6.27105 5.22084 6.0962 5.81531C6.03792 6.02513 6.03792 6.58464 6.0962 6.79446C6.23608 7.26071 6.57412 7.692 6.99375 7.90182C7.32013 8.07666 7.47166 8.11163 7.86798 8.11163C8.2643 8.11163 8.40418 8.07666 8.74221 7.90182C9.15019 7.692 9.49988 7.26071 9.63976 6.79446C9.69804 6.58464 9.69804 6.02513 9.63976 5.81531C9.46491 5.20918 8.97534 4.71961 8.36921 4.54476C8.14773 4.47483 7.57657 4.48648 7.3551 4.54476L7.34344 4.55642Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M19.1166 0.150339C18.4988 0.313529 17.9626 0.838068 17.811 1.46752C17.7877 1.56077 17.7644 1.75893 17.7644 1.92212C17.7644 2.45831 17.9159 2.84298 18.2889 3.21598C18.6619 3.58899 19.0466 3.74052 19.5828 3.74052C19.746 3.74052 19.9442 3.71721 20.0374 3.6939C20.6669 3.53071 21.2031 3.00617 21.3546 2.37672C21.4129 2.15525 21.4129 1.67733 21.3546 1.45586C21.1914 0.826412 20.6669 0.290216 20.0374 0.138682C19.8276 0.0804002 19.3147 0.0804002 19.1049 0.138682L19.1166 0.150339Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M30.5399 6.13007C29.0829 6.2816 27.9755 6.63129 26.8798 7.28405C25.924 7.85522 24.8982 8.85767 24.3504 9.75522C23.6976 10.816 23.3479 11.7951 23.1964 13.0074C23.1264 13.5552 23.1381 14.9074 23.1964 15.5018C23.3246 16.5276 23.5344 17.2153 23.9424 18.0546C24.2338 18.6491 24.4086 18.9172 24.7817 19.3834C25.9939 20.8755 27.8939 21.9712 29.8405 22.2975C30.2136 22.3558 30.3301 22.3675 31.2626 22.3675C32.3816 22.3675 32.7313 22.3325 33.489 22.1577C34.7362 21.8546 35.6804 21.4 36.8577 20.5374C37.0442 20.3975 37.324 20.1994 37.4639 20.0945C37.662 19.9546 37.7319 19.8847 37.7319 19.8497C37.7319 19.7914 37.6504 19.6049 37.6154 19.5932C37.6037 19.5932 37.5921 19.5699 37.5921 19.5583C37.5921 19.5233 37.3589 19.1386 37.254 18.9988C37.2191 18.9521 37.1958 18.9055 37.2074 18.9055C37.2074 18.9055 37.2074 18.8822 37.1724 18.8472C37.1375 18.8123 37.1258 18.7773 37.1258 18.7656V18.7423C37.1142 18.7423 37.0908 18.719 37.0792 18.684C37.0442 18.6141 36.7528 18.1362 36.6129 17.9147L36.5197 17.7632L35.5872 18.381C34.8994 18.8472 34.5614 19.0571 34.2583 19.1969C33.7571 19.4417 33.1976 19.6282 32.6731 19.7331C32.3117 19.8031 32.2068 19.8147 31.6123 19.8147C30.7964 19.8147 30.4933 19.7681 29.7706 19.535C29.3743 19.4067 28.5933 19.0104 28.2786 18.7773C27.9405 18.5325 27.4277 18.0196 27.2062 17.7166C26.7516 17.0988 26.4718 16.5159 26.297 15.8282C26.1571 15.2571 26.0988 14.8258 26.0988 14.2546C26.0988 13.6834 26.1571 13.2405 26.3086 12.646C26.4718 12.0282 26.6117 11.6902 26.9264 11.154C27.754 9.79019 29.0363 8.95092 30.6099 8.74111C30.9712 8.69448 31.7988 8.68282 32.1951 8.72945C32.5798 8.77608 33.0228 8.88098 33.2792 8.99755C33.6755 9.18405 34.0485 9.48712 34.2117 9.79019C34.3399 10 34.4681 10.6061 34.4681 10.9675V11.1423H36.9976V10.2914C36.9976 9.33559 36.9743 9.12577 36.7994 8.68282C36.543 8.01841 35.8669 7.31902 35.1326 6.94602C34.7013 6.72454 34.0369 6.49141 33.5123 6.37485C32.8829 6.22332 32.5448 6.18835 31.5774 6.18835C31.0761 6.18835 30.6099 6.18835 30.5399 6.18835V6.13007Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M68.6913 6.20001C68.0969 6.28161 67.584 6.57302 67.2459 7.01596C67.0361 7.29572 66.8729 7.6221 66.6165 8.37976C66.2785 9.34725 65.4858 11.5153 64.2736 14.8025C63.6791 16.4227 63.1312 17.938 63.0613 18.1945C62.9913 18.4509 62.9214 18.6724 62.9098 18.6841C62.8981 18.6957 62.6067 17.9847 62.2337 17.0522C61.068 14.0798 60.2288 11.9699 59.5061 10.1982C59.1098 9.24234 58.7018 8.23989 58.5969 7.96013C58.3637 7.36565 58.3171 7.28406 58.1306 7.01596C57.8742 6.64296 57.5711 6.40983 57.1631 6.28161C56.9533 6.21167 56.93 6.21167 55.7294 6.21167H54.5055V8.85768H54.9717C55.5196 8.85768 55.6128 8.89265 55.811 9.11412C55.9159 9.24234 56.0791 9.60369 57.2214 12.2963C57.9325 13.9749 58.795 16.0147 59.1564 16.854C59.5177 17.6933 59.9607 18.719 60.1355 19.1503C60.6717 20.4325 61.3361 22.0528 61.3478 22.0877C61.3478 22.0994 61.3594 22.1577 61.3711 22.2043C61.3944 22.3325 61.3245 22.5656 61.0214 23.3C60.4386 24.6988 60.1355 25.2 59.5644 25.7129C58.795 26.389 58.0024 26.5988 57.1748 26.3307C56.9183 26.2491 56.5104 26.0509 55.9159 25.7245C55.6361 25.573 55.403 25.4564 55.3913 25.4681C55.3913 25.4681 55.2515 25.6896 55.1116 25.946C54.9601 26.2025 54.8318 26.4239 54.8202 26.4472C54.7619 26.5172 54.2024 27.5546 54.2024 27.5896C54.2024 27.6712 54.8435 28.1141 55.3564 28.3706C56.2073 28.8018 57.0466 29 58.0257 29C59.0048 29 59.6809 28.8368 60.4736 28.4405C61.7208 27.811 62.5601 26.762 63.376 24.7687C63.7257 23.9178 65.0312 20.7006 66.9895 15.8632C69.3791 9.94173 69.3091 10.1166 69.5423 9.65032C69.7637 9.21903 69.857 9.10246 70.1251 8.96259C70.265 8.89265 70.3116 8.88099 70.6613 8.86933H71.0459V6.18835H69.9736C69.3791 6.18835 68.8196 6.18835 68.7263 6.20001H68.6913Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M16.4357 6.21167C16.4357 6.21167 16.4124 6.77118 16.4124 7.4356V8.63621H17.17C18.0093 8.63621 18.0559 8.63621 18.2424 8.84602C18.4406 9.05584 18.4289 8.58958 18.4289 14.5344C18.4289 20.1061 18.4289 20.7822 18.5338 21.1785C18.6854 21.773 19.0351 22.1227 19.5829 22.2509C19.8976 22.3209 21.378 22.3908 21.413 22.3209C21.413 22.3209 21.413 19.0104 21.413 14.989L21.3897 7.68038L21.3314 7.45891C21.1915 6.94603 20.9351 6.59633 20.562 6.42149C20.1774 6.22333 20.1541 6.22333 18.2074 6.21167C17.24 6.21167 16.4473 6.21167 16.4357 6.21167Z', fill: '#FFF' }),
    React.createElement('path', { d: 'M38.5829 6.22332C38.5829 6.22332 38.5829 6.82946 38.5829 7.52884V8.78774H39.0958C39.4805 8.78774 39.6437 8.79939 39.7836 8.83436C40.2848 8.97424 40.5645 9.19571 40.9725 9.80185C42.2197 11.6203 43.7467 13.8816 43.7934 13.9632C43.8167 14.0098 43.84 14.0681 43.84 14.0798C43.84 14.0914 42.6277 15.9564 41.159 18.2061C39.6786 20.4558 38.478 22.3092 38.478 22.3209C38.478 22.3325 39.2474 22.3209 40.1799 22.3209H41.8817L43.7817 19.3485C44.8191 17.7166 45.6934 16.3644 45.7167 16.3295C45.7516 16.2828 45.7983 16.3295 46.148 16.8423C46.3578 17.1571 47.2553 18.5092 48.1296 19.8614L49.7148 22.3092H51.405C53.0136 22.3092 53.0952 22.3092 53.0719 22.2626C53.0602 22.2393 51.848 20.3742 50.3793 18.1362C48.0713 14.5926 47.7216 14.0448 47.7449 13.9982C47.7566 13.9632 48.386 13.0074 49.132 11.8767C50.5541 9.73191 50.7056 9.52209 51.0903 9.21903C51.1835 9.14909 51.37 9.03252 51.5099 8.97424L51.7664 8.85768H52.3842L53.0019 8.83436V6.18835H51.8713C50.6473 6.18835 50.4375 6.20001 50.0179 6.33989C49.5983 6.47976 49.2719 6.66627 48.9572 6.99265C48.7473 7.21412 48.6308 7.36565 48.2927 7.94847C47.8032 8.77608 45.8332 11.9117 45.8099 11.9C45.7516 11.865 45.0639 10.8043 43.5486 8.35645C42.8842 7.29571 42.7559 7.12087 42.5112 6.87608C41.9283 6.31657 41.3105 6.1767 39.4105 6.1767C38.8161 6.1767 38.6412 6.1767 38.6296 6.21167L38.5829 6.22332Z', fill: '#FFF' })
  );
}

// ─── COMPONENTS ─────────────────────────────────────────────────────────────

function ServiceCard(props) {
  var service = props.service;
  var videoRef = React.useRef(null);
  var cardRef = React.useRef(null);

  React.useEffect(function () {
    var card = cardRef.current;
    var video = videoRef.current;
    if (!card || !video) return;

    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    function playVideo() {
      card.classList.add('is-video-active');
      video.muted = true;
      var p = video.play();
      if (p !== undefined) {
        p.catch(function () {
          card.classList.remove('is-video-active');
        });
      }
    }

    function stopVideo() {
      card.classList.remove('is-video-active');
      video.pause();
      try { video.currentTime = 0; } catch (e) { }
    }

    card.addEventListener('mouseenter', playVideo);
    card.addEventListener('mouseleave', stopVideo);
    card.addEventListener('focusin', playVideo);
    card.addEventListener('focusout', stopVideo);

    stopVideo();

    return function () {
      card.removeEventListener('mouseenter', playVideo);
      card.removeEventListener('mouseleave', stopVideo);
      card.removeEventListener('focusin', playVideo);
      card.removeEventListener('focusout', stopVideo);
    };
  }, []);

  return React.createElement(
    Link,
    { href: service.href, passHref: true },
    React.createElement(
      'a',
      { className: service.blockClass, ref: cardRef },

      // Media layer
      React.createElement(
        'div', { className: 'block-media', 'aria-hidden': 'true' },
        React.createElement('div', {
          className: 'block-image',
          style: { backgroundImage: "url('" + service.image + "')" }
        }),
        React.createElement(
          'video',
          { className: 'block-video', muted: true, loop: true, playsInline: true, preload: 'none', ref: videoRef },
          React.createElement('source', { src: service.videoSrc, type: 'video/mp4' })
        ),
        React.createElement('div', { className: 'block-media-overlay' })
      ),

      React.createElement('div', { className: 'block-glow' }),
      React.createElement('div', { className: 'block-accent-line' }),
      React.createElement('div', { className: 'block-icon-bg' }, service.icon),

      // Content
      React.createElement(
        'div', { className: 'block-content' },

        // Tag row
        React.createElement(
          'div', { className: 'block-tag' },
          React.createElement('div', { className: 'block-tag-dot' }),
          React.createElement('span', { className: 'block-tag-label' }, service.tag),
          service.live && React.createElement(
            'span', { className: 'live-badge' },
            React.createElement('span', { className: 'live-dot' }),
            'Live'
          )
        ),

        React.createElement('h2', { className: 'block-title' }, service.title),
        React.createElement('p', { className: 'block-desc' }, service.desc),

        React.createElement(
          'div', { className: 'block-cta' },
          service.cta,
          React.createElement('div', { className: 'block-arrow' }, '→')
        )
      )
    )
  );
}

function HeroSection(props) {
  var servicesRef = props.servicesRef;
  var heroRef = React.useRef(null);

  React.useEffect(function () {
    // var autoScrollDone = false;

    // function autoScrollToNextSection() {
    //   if (autoScrollDone) return;
    //   var hero = heroRef.current;
    //   var next = servicesRef.current;
    //   if (!hero || !next) return;
    //   var heroBottom = hero.offsetTop + hero.offsetHeight;
    //   var stillInHero = window.scrollY < heroBottom - 120;
    //   if (stillInHero) {
    //     autoScrollDone = true;
    //     next.scrollIntoView({ behavior: 'smooth', block: 'start' });
    //   }
    // }

    // var timer = setTimeout(autoScrollToNextSection, 5000);

    // function cancelAutoScroll() {
    //   clearTimeout(timer);
    // }

    // ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach(function (ev) {
    //   window.addEventListener(ev, cancelAutoScroll, { once: true, passive: true });
    // });

    // return function () { clearTimeout(timer); };
  }, [servicesRef]);

  // function handleScrollCueClick() {
  //   if (servicesRef.current) {
  //     servicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   }
  // }

  return React.createElement(
    'section', { className: 'hero', ref: heroRef },


    // Logo
    React.createElement(
      'a', { href: '#', className: 'hero-logo' },
      React.createElement(PicxyLogo)
    ),

    // Background video + fallback
    React.createElement(
      'div', { className: 'hero-media', 'aria-hidden': 'true' },
      React.createElement(
        'video', { className: 'hero-video', autoPlay: true, muted: true, loop: true, playsInline: true },
        React.createElement('source', { src: '/static/img/main-video.mp4', type: 'video/mp4' })
      ),
      React.createElement('div', { className: 'hero-video-fallback' })
    ),

    React.createElement('div', { className: 'hero-grid' }),
    React.createElement('div', { className: 'hero-overlay' }),
    React.createElement('div', { className: 'hero-glow' }),

    // Banner blocks
    React.createElement(
      'div', { className: 'banner-blocks', 'aria-hidden': 'false' },
      // React.createElement(
      //   Link,
      //   { href: '/stock-images', passHref: true },
      //   React.createElement(
      //     'a',
      //     {
      //       style: {
      //         display: 'flex',
      //         alignItems: 'center',
      //         gap: '8px'
      //       }
      //     },

      //     [
      //       React.createElement('img', {
      //         src: '/static/img/stock-images.svg', // your icon path
      //         alt: 'Stock Images'
      //       }),
      //       'Stock Images'
      //     ]
      //   )
      // ),
      React.createElement(
        Link,
        { href: '/book_photographer', passHref: true },
        React.createElement(
          'a',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }
          },
          [
            React.createElement('img', {
              src: '/static/img/book-photography.svg', // your icon path
              alt: 'Book Photographer'
            }),
            'Book Photographer'
          ]
        )
      ),
      React.createElement(
        Link,
        { href: '/ai_face_sync', passHref: true },
        React.createElement(
          'a',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }
          },
          [
            React.createElement('img', {
              src: '/static/img/ai-face.svg', // icon path
              alt: 'AI Face Sync'
            }),
            'AI Face Sync'
          ]
        )
      ),

      React.createElement(
        Link,
        { href: '/instashot', passHref: true },
        React.createElement(
          'a',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }
          },
          [
            React.createElement('img', {
              src: '/static/img/instashot.svg', // icon path
              alt: 'Instashot'
            }),
            'Instashot'
          ]
        )
      )
    ),

    // Hero text
    React.createElement(
      'div', { className: 'hero-content-wrap' },
      React.createElement(
        'div', { className: 'hero-content' },
        // React.createElement(
        //   'div', { className: 'hero-eyebrow' },
        //   React.createElement('div', { className: 'eyebrow-dot' }),
        //   React.createElement('span', { className: 'eyebrow-text' }, "India's Visual Universe")
        // ),
        // React.createElement(
        //   'h1', { className: 'hero-h1' },
        //   'Where Every',
        //   React.createElement('br'),
        //   React.createElement('em', null, 'Visual Story'),
        //   ' Begins'
        // ),
         React.createElement(
          'h1', { className: 'hero-h1' },
          'Built For Every',
          React.createElement('br'),
          React.createElement('em', null,  'Visual Story' ),
          ' Worth Telling'
        ),
       
        
        React.createElement(
        Link,
        { href: 'https://api.whatsapp.com/send?phone=918885558235&text=Hi%20Picxy%21%20I%27m%20interested%20in%20your%20services.%20Could%20you%20please%20share%20more%20details%3F' },
        React.createElement(
          'a',
          { 
            className: 'btn btn-primary landing-page-btn',
            target: '_blank',
            rel: 'noopener noreferrer'  // recommended for security
          },
          'Talk to Experts'
        )
      )

      )
    ),

    // Scroll cue
    // React.createElement(
    //   'div', { className: 'scroll-cue', onClick: handleScrollCueClick },
    //   React.createElement('span', { className: 'scroll-cue-label' }, 'Explore'),
    //   React.createElement('div', { className: 'scroll-cue-arrow' })
    // )
  );
}

function ServicesSection(props) {
  return React.createElement(
    'section', { className: 'services', id: 'services', ref: props.servicesRef },
    SERVICES.map(function (service) {
      return React.createElement(ServiceCard, { key: service.id, service: service });
    }),
    React.createElement(
      'div',
      { className: 'services-highlight' },
      React.createElement(
        'h2',
        { className: 'services-highlight-title' },
        'Picxy lets you enjoy your event while we take complete ownership of preserving every memory.'
      )
    )
  );
}

// ─── ROOT APP ────────────────────────────────────────────────────────────────
function App() {
  var servicesRef = React.useRef(null);

  // Inject styles once on mount
  React.useEffect(function () {
    var existing = document.getElementById('picxy-styles');
    if (existing) return;
    var styleEl = document.createElement('style');
    styleEl.id = 'picxy-styles';
    styleEl.textContent = CSS;
    document.head.appendChild(styleEl);
    return function () {
      var el = document.getElementById('picxy-styles');
      if (el) el.remove();
    };
  }, []);

  return React.createElement(
    React.Fragment, null,
    React.createElement(LicenseNotice, null),
    React.createElement(HeroSection, { servicesRef: servicesRef }),
    React.createElement(ServicesSection, { servicesRef: servicesRef })
  );
}

export default App;
